/** Small presentational helpers shared by screens and overlays. */
import type { SeatMirror, TableView } from '../net/types-local';

export type TeamIdx = 0 | 1;

export const teamOfSeat = (seat: number): TeamIdx => (seat % 2) as TeamIdx;

export function teamLabel(team: TeamIdx, mySeat: number | null): string {
  if (mySeat == null) return team === 0 ? 'Blue' : 'Red';
  return teamOfSeat(mySeat) === team ? 'Us' : 'Them';
}

export function teamColorVar(team: TeamIdx): string {
  return team === 0 ? 'var(--team-0)' : 'var(--team-1)';
}

export function seatDisplayName(seat: SeatMirror | undefined, fallback = 'Open seat'): string {
  if (!seat || seat.controller === 'empty') return fallback;
  return seat.name || (seat.controller === 'bot' ? 'Bot' : 'Player');
}

export function handsTotal(table: TableView | null): number | null {
  if (!table?.rulesJson) return 4;
  try {
    const r = JSON.parse(table.rulesJson) as { format?: { kind?: string; hands?: number } };
    if (r.format?.kind === 'quick') return r.format.hands ?? 4;
    return null; // classic: to target
  } catch {
    return 4;
  }
}

export function formatSeconds(ms: number): string {
  return `${Math.max(0, Math.ceil(ms / 1000))}s`;
}

export function copyText(text: string): Promise<boolean> {
  try {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => false,
    );
  } catch {
    return Promise.resolve(false);
  }
}

export function bidLabel(bid: number): string {
  if (bid < 0) return '?';
  if (bid === 20) return 'NIL';
  if (bid === 21) return 'BLIND';
  return String(bid);
}

/**
 * How full a phase timer bar should be, 0..1.
 *
 * `remainingMs` must be measured against `serverNow()`, not `Date.now()`: `endsAt` is the server's
 * clock, and a device even a minute off would otherwise report every bar as drained (review #15).
 *
 * `full` is the window the server armed (`timer.durationMs`), so a time-bank extension lengthens
 * the bar instead of overflowing it. Callers fall back to the table's own rules when the server
 * sent no duration; the House Rules numbers (30s/15s) are never assumed, because a Blitz table runs
 * 20s/10s and a forced play 3s, and measuring those against 30s/15s opened the bars part-full and
 * never filled them.
 * Still clamped, so a stale duration is wrong by a little rather than by a lot.
 */
export function timerFraction(remainingMs: number, fullMs: number): number {
  if (!Number.isFinite(fullMs) || fullMs <= 0) return 0;
  return Math.min(1, Math.max(0, remainingMs / fullMs));
}
